import { useSelector } from 'react-redux';

import { RootState } from '../lib/store';
import { isCurrentAccountAwaitingApproval } from '../lib/store/account-flow/selectors';
import { getAccountExpirationDate, hasAccountOnlyDemoPlan } from '../lib/store/crm/helpers';
import MarketStateCache from '../lib/store-util/MarketStateCache';
import SymbolStatusCache from '../lib/store-util/SymbolStatusCache';
import { checkIfSymbolIsHalted } from '../lib/util/HaltSymbolHelper';
import { isMarketRegularHours } from '../lib/util/MarketDataHelpers';

import { ACCOUNT_SETUP_NOTIFICATION_ID } from '../components/AccountSetupNotification/AccountSetupNotification';

import useElementVisibilityById from './useElementVisibilityById';
import { useTimeRemaining } from './useTimeRemaining';

type AccountStatusData = {
  dailyResetProcessing: boolean;
  dailyLossReached: boolean;
  totalLossReached: boolean;
};

type UseTradingButtonsDisabledParams = {
  symbol: string;
  accountStatusData: AccountStatusData;
  additionalCondition?: boolean;
};

/**
 * Custom hook to determine if trading buttons should be disabled.
 *
 * Buttons are disabled when any of these conditions are true:
 * - Market is not in regular trading hours
 * - Symbol is halted
 * - Documents reviewing notification is visible
 * - Funded account setup notification is visible
 * - Daily reset is processing
 * - Daily loss limit reached
 * - Total loss limit reached
 * - Crypto plan payment is pending
 * - The account is a demo/demo-only account whose plan period has run out
 * - Any additional condition passed (e.g., quantity <= 0)
 *
 * @param params - Configuration object
 * @param params.symbol - The trading symbol to check
 * @param params.accountStatusData - Account status containing loss limits and reset state
 * @param params.additionalCondition - Optional additional condition to disable buttons (e.g., quantity <= 0)
 * @returns boolean indicating whether trading buttons should be disabled
 */
function useTradingButtonsDisabled(params: UseTradingButtonsDisabledParams): boolean {
  const { symbol, accountStatusData, additionalCondition = false } = params;
  const { dailyResetProcessing, dailyLossReached, totalLossReached } = accountStatusData;

  const { marketState } = MarketStateCache.use();
  const isMarketHours = isMarketRegularHours(marketState);

  const symbolsStatusCache = SymbolStatusCache.use(symbol);
  const isSymbolHalted = checkIfSymbolIsHalted(symbolsStatusCache);

  const isDailyAutoClose = useSelector((state: RootState) => state.closeout.isDailyAutoClose);
  const isCryptoPaymentPending = useSelector((state: RootState) => state.payment.isSolanaPaymentPending);

  const isAwaitingApproval = useSelector(isCurrentAccountAwaitingApproval);
  const isAccountSetupNotification = useElementVisibilityById(ACCOUNT_SETUP_NOTIFICATION_ID);

  const isUseDemoAccountOrHasOnlyDemoAccount = useSelector((state: RootState) =>
    state.crm.isUseDemoAccount || hasAccountOnlyDemoPlan(state.crm));
  const expirationDate = useSelector((state: RootState) => getAccountExpirationDate(state));
  const { hasNoRemainingTime } = useTimeRemaining(expirationDate);
  const isExpiredDemoAccount = isUseDemoAccountOrHasOnlyDemoAccount && hasNoRemainingTime;

  const isButtonsDisabled = !isMarketHours
    || isSymbolHalted
    || isAwaitingApproval
    || isAccountSetupNotification
    || dailyResetProcessing
    || dailyLossReached
    || totalLossReached
    || isDailyAutoClose
    || additionalCondition
    || isExpiredDemoAccount
    || isCryptoPaymentPending === true;

  return isButtonsDisabled;
}

export default useTradingButtonsDisabled;
